// src/tests/ChildTest/NonwordRepetition/Test.js
import React, {Component} from 'react';
import strings from 'strings';
import listen from 'assets/listen.png';
import {updateStepData} from 'api';
import {OBJECT_IMAGE_MAP, SHAPE_IMAGE_MAP} from './assets';
import {
	getChildTestPack,
	getChildSayAgain,
	noDistractionsSounds,
	nnDistractionsSounds,
} from './assets';
import './styles.scss';
import AudioPlayer from 'components/AudioPlayer';

// Helper to get random int inclusive
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

class Test extends Component {
	constructor(props) {
		super(props);
		const langCode = strings.getLanguage();
		const {testKeyType} = this.props;
		this.assets = getChildTestPack(langCode, testKeyType);
		this.distractionVoiceOvers =
			langCode === 'nn' ? nnDistractionsSounds : noDistractionsSounds;
		this.sayAgain = getChildSayAgain(langCode); // generic "say it again"
		this.distractionTypeSequence = this.buildDistractionTypeSequence();
		this.state = {
			isInDelay: false, // true during the 15s distraction window
			distraction: null, // { type, ...payload }
			isPlayingAudio: false,
		};
	}

	componentDidMount() {
		this.initializeAudio();
	}

	componentWillUnmount() {
		this.stopSounds();
		clearTimeout(this.sayAgainTimeout);
	}

	componentDidUpdate(prevProps) {
		// New item: play new nonword
		if (prevProps.currentStep !== this.props.currentStep) {
			this.startNewTask(this.props.currentStep);
			return;
		}

		// Same item – watch for admin having scored the immediate response
		const prevSD = prevProps.stepData || {};
		const nextSD = this.props.stepData || {};

		if (nextSD.nonwordImmediateDone && !prevSD.nonwordImmediateDone) {
			// Start 10s distraction, then play "si ordet en gang til"
			this.scheduleDelayedCue();
		}
	}

	buildDistractionTypeSequence = () => {
		// 8 “core” tasks used in the manual – each will occur twice = 16 items
		const baseTypes = [
			'CLAP_COUNT', // task 1
			'COLOR_CIRCLES', // task 2
			'NUMBER_LIST', // task 3
			'SAME_COLOR', // task 4
			'NAME_ANIMALS', // task 5
			'NAME_OBJECTS', // task 6 (Kelly)
			'SIZE_ORDER', // task 7 (Kelly, different sizes)
			'SHAPE_ANALOGY', // task 8 (shapes)
			// Task 9 “Løs oppgaven” can be added later as MATH_PROBLEM if you want
		];

		const seq = baseTypes.concat(baseTypes); // 16 entries

		// Shuffle with Fisher–Yates so order is unpredictable per test run
		for (let i = seq.length - 1; i > 0; i--) {
			const j = randInt(0, i);
			const tmp = seq[i];
			seq[i] = seq[j];
			seq[j] = tmp;
		}

		return seq;
	};

	getDistractionTypeForStep = (stepIndex) => {
		const seq = this.distractionTypeSequence;
		if (!seq || !seq.length) return 'NAME_ANIMALS'; // fallback

		// If there ever are more than 16 items, we just wrap around
		return seq[stepIndex % seq.length];
	};

	initializeAudio = () => {
		// preload nonword clips + say-again cue
		Object.values(this.assets).forEach((a) => a && a.load && a.load());
		if (this.sayAgain && this.sayAgain.load) this.sayAgain.load();
		setTimeout(() => this.startNewTask(this.props.currentStep), 500);
	};

	startNewTask = (idx) => {
		this.stopSounds();
		clearTimeout(this.sayAgainTimeout);

		// New item → clear admin flag & distraction
		updateStepData({nonwordImmediateDone: false});

		this.setState({
			isInDelay: false,
			distraction: null,
		});

		const sound = this.assets[idx];
		if (!sound) {
			console.warn('Missing audio for nonword test step', idx);
			return;
		}

		if (sound && sound.play) {
			this.setState({isPlayingAudio: true});
			if (sound.once) {
				sound.once('end', () => {
					this.setState({isPlayingAudio: false});
				});
			}
			sound.play();
		}

		// We now wait for admin to score the immediate response.
		// When that happens, componentDidUpdate -> scheduleDelayedCue().
	};

	// -------- distraction generation --------

	createRandomDistraction = (stepIndex) => {
		const {testKeyType} = this.props || {};

		// Decide if this is a "Y/A" vs "C" style test from testKeyType
		const tk = String(testKeyType || '').toUpperCase();
		const isYA =
			tk.includes('Y/A') ||
			tk.includes('YOUTH') ||
			tk.includes('UNGDOM') ||
			tk.includes('ADULT') ||
			tk.includes('VOKSEN') ||
			tk.includes('YA');

		const type = this.getDistractionTypeForStep(stepIndex);

		switch (type) {
			case 'CLAP_COUNT': {
				// Task 1: Klapp hendene så mange ganger som tallet på skjermen viser (5–12)
				const n = randInt(5, 12);
				return {type, n};
			}

			case 'COLOR_CIRCLES': {
				// Task 2: Si navnet på fargene du ser på skjermen
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'purple',
					'orange',
				];
				const count = randInt(4, 6);
				const chosen = [];
				while (chosen.length < count) {
					const c = colors[randInt(0, colors.length - 1)];
					if (!chosen.includes(c)) chosen.push(c);
				}
				return {type, colors: chosen};
			}

			case 'NUMBER_LIST': {
				// Task 3: Si tallene du ser på skjermen
				// Dysmate C: 1–100, Dysmate Y/A: 100–1000 :contentReference[oaicite:2]{index=2}
				const min = isYA ? 100 : 1;
				const max = isYA ? 1000 : 100;
				const howMany = isYA ? randInt(4, 6) : randInt(3, 5);

				const numbers = [];
				while (numbers.length < howMany) {
					const num = randInt(min, max);
					if (!numbers.includes(num)) numbers.push(num);
				}
				return {type, numbers};
			}

			case 'SAME_COLOR': {
				// Task 4: Nev tre gjenstander som har samme farge som skjermen viser :contentReference[oaicite:3]{index=3}
				const colors = [
					'red',
					'green',
					'blue',
					'yellow',
					'brown',
					'orange',
				];
				const color = colors[randInt(0, colors.length - 1)];
				return {type, color};
			}

			case 'NAME_ANIMALS': {
				// Task 5: Nev fire dyr så raskt du klarer (no visuals needed) :contentReference[oaicite:4]{index=4}
				return {type};
			}

			case 'NAME_OBJECTS': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const minCount = isYA ? 6 : 4;
				const maxCount = isYA ? 8 : 6;
				const count = randInt(minCount, maxCount);

				const items = [];
				while (
					items.length < count &&
					items.length < allLabels.length
				) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!items.includes(label)) items.push(label);
				}
				return {type, items};
			}

			case 'SIZE_ORDER': {
				const allLabels = Object.keys(OBJECT_IMAGE_MAP);
				const count = randInt(3, Math.min(6, allLabels.length));

				const items = [];
				while (items.length < count) {
					const idx = randInt(0, allLabels.length - 1);
					const label = allLabels[idx];
					if (!items.find((x) => x.label === label)) {
						items.push({
							label,
							sizeRank: randInt(1, 5),
						});
					}
				}
				items.sort((a, b) => b.sizeRank - a.sizeRank);
				return {type, items};
			}

			case 'SHAPE_ANALOGY': {
				const shapes = Object.keys(SHAPE_IMAGE_MAP); // ['triangle','square','circle']
				const shape = shapes[randInt(0, shapes.length - 1)];
				return {type, shape};
			}

			// Task 9 “Løs oppgaven som står på skjermen” could be added later as:
			// case 'MATH_PROBLEM': { ... }

			default:
				return {type: 'NAME_ANIMALS'};
		}
	};

	scheduleDelayedCue = () => {
		clearTimeout(this.sayAgainTimeout);

		const stepIndex = this.props.currentStep || 0;
		const distraction = this.createRandomDistraction(stepIndex);

		this.setState({
			isInDelay: true,
			distraction,
		});

		// Run distraction for 15 seconds, then ask to repeat the word
		this.sayAgainTimeout = setTimeout(() => {
			this.setState({isInDelay: false, distraction: null});

			if (this.sayAgain && this.sayAgain.play) {
				this.setState({isPlayingAudio: true});
				if (this.sayAgain.once) {
					this.sayAgain.once('end', () => {
						this.setState({isPlayingAudio: false});
					});
				}
				this.sayAgain.play();
			}
		}, 15000); // 15s
	};

	stopSounds = () => {
		const stop = (s) => {
			if (!s) return;
			if (s.stop) s.stop();
			else if (s.pause) s.pause();
		};

		Object.values(this.assets).forEach(stop);
		if (this.sayAgain) stop(this.sayAgain);
		this.setState({isPlayingAudio: false});
	};

	// -------- render helpers --------

	renderDistraction = () => {
		const {distraction} = this.state;
		if (!distraction) return null;

		switch (distraction.type) {
			case 'CLAP_COUNT':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction1}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__big-number">
							{distraction.n}
						</div>
					</div>
				);

			case 'COLOR_CIRCLES':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction2}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__circles">
							{distraction.colors.map((c) => (
								<div
									key={c}
									className="nw-distraction__circle"
									style={{backgroundColor: c}}
								/>
							))}
						</div>
					</div>
				);

			case 'NUMBER_LIST':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction3}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__numbers">
							{distraction.numbers.map((n) => (
								<span
									key={n}
									className="nw-distraction__number"
								>
									{n}
								</span>
							))}
						</div>
					</div>
				);

			case 'SAME_COLOR':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction6}
								styles={{display: 'none'}}
							/>
						</p>
						<div
							className="nw-distraction__color-block"
							style={{backgroundColor: distraction.color}}
						/>
					</div>
				);

			case 'NAME_OBJECTS':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction7}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((label, idx) => {
								const src = OBJECT_IMAGE_MAP[label];
								if (!src) return null; // should not happen anymore
								return (
									<div
										key={`${label}-${idx}`}
										className="nw-distraction__object-imageWrapper"
									>
										<img
											src={src}
											alt={label}
											className="nw-distraction__object-image"
										/>
									</div>
								);
							})}
						</div>
					</div>
				);

			case 'SIZE_ORDER':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction8}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__objects nw-distraction__objects--images">
							{distraction.items.map((item, idx) => {
								const src = OBJECT_IMAGE_MAP[item.label];
								if (!src) return null;
								const scale = 1 + item.sizeRank * 0.12;

								return (
									<div
										key={`${item.label}-${idx}`}
										className="nw-distraction__object-imageWrapper nw-distraction__object-imageWrapper--sized"
										style={{transform: `scale(${scale})`}}
									>
										<img
											src={src}
											alt={item.label}
											className="nw-distraction__object-image"
										/>
									</div>
								);
							})}
						</div>
					</div>
				);

			case 'SHAPE_ANALOGY':
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction4}
								styles={{display: 'none'}}
							/>
						</p>
						<div className="nw-distraction__shape-wrapper">
							{(() => {
								const src = SHAPE_IMAGE_MAP[distraction.shape];
								if (!src) return null;
								return (
									<img
										src={src}
										alt={distraction.shape}
										className="nw-distraction__shape-image"
									/>
								);
							})()}
						</div>
					</div>
				);

			case 'NAME_ANIMALS':
			default:
				return (
					<div className="nw-distraction">
						<p className="nw-distraction__instruction">
							<AudioPlayer
								autoplay
								inline
								src={this.distractionVoiceOvers.distraction5}
								styles={{display: 'none'}}
							/>
						</p>
					</div>
				);
		}
	};

	render() {
		const {isInDelay, isPlayingAudio} = this.state;

		return (
			<div className="NonwordRepetition">
				{isPlayingAudio && (
					<img className="listen-image" src={listen} alt="" />
				)}

				{/* Normal instruction text when NOT in the 10s distraction */}
				{!isInDelay && (
					<p>
						{strings.nonword_test_listen_repeat ||
							strings.sm_test_listen_repeat}
					</p>
				)}

				{/* 10s distraction between immediate and delayed repetition */}
				{isInDelay && this.renderDistraction()}
			</div>
		);
	}
}

export default Test;
